import { useMutation, useQuery } from '@tanstack/react-query';
import apiClient from './client';
import type { AnalysisResult, RiskScore, IOCResult, CVEInfo, ThreatFeedResponse, ScanResult, EntityType } from '../types';

interface ExtractEntitiesPayload {
  text: string;
  language?: string;
  entity_types?: string[];
}

interface InferRelationshipsPayload {
  entity_ids: string[];
  max_depth?: number;
  min_confidence?: number;
}

// ── IOC type auto-detection ──

type IOCType = 'ip' | 'domain' | 'url' | 'hash_md5' | 'hash_sha1' | 'hash_sha256' | 'email' | 'cve';

function detectIOCType(indicator: string): IOCType {
  const v = indicator.trim();
  if (/^CVE-\d{4}-\d+$/i.test(v)) return 'cve';
  if (/^[a-f0-9]{64}$/i.test(v)) return 'hash_sha256';
  if (/^[a-f0-9]{40}$/i.test(v)) return 'hash_sha1';
  if (/^[a-f0-9]{32}$/i.test(v)) return 'hash_md5';
  if (/^https?:\/\//i.test(v)) return 'url';
  if (/^[\w.+-]+@[\w.-]+\.\w+$/.test(v)) return 'email';
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(v)) return 'ip';
  return 'domain';
}

// ── API Functions ──

async function extractEntities(payload: ExtractEntitiesPayload): Promise<AnalysisResult> {
  const { data } = await apiClient.post('/analysis/entities', payload);
  // Map backend response to frontend AnalysisResult format
  return {
    id: data.job_id || data.id || `job-${Date.now()}`,
    type: 'entity_extraction',
    entities: (data.entities || []).map((e: Record<string, unknown>) => ({
      name: e.name || e.text || '',
      type: (e.entity_type || e.type || 'unknown') as EntityType,
      confidence: (e.confidence as number) ?? 0.5,
      span: [e.start_offset ?? 0, e.end_offset ?? 0] as [number, number],
      source_text: e.context as string | undefined,
    })),
    relationships: [],
    summary: `${(data.entities || []).length} entities extracted from ${data.text_length ?? 0} characters`,
    confidence: (data.entities || []).length > 0
      ? (data.entities as Array<{ confidence?: number }>).reduce((s: number, e: { confidence?: number }) => s + (e.confidence ?? 0), 0) / (data.entities as unknown[]).length
      : 0,
    processing_time_ms: data.processing_time_ms ?? 0,
    created_at: new Date().toISOString(),
  };
}

async function inferRelationships(payload: InferRelationshipsPayload): Promise<AnalysisResult> {
  const { data } = await apiClient.post('/analysis/relationships', {
    entity_ids: payload.entity_ids,
    inference_depth: payload.max_depth ?? 1,
    text_context: null,
  });
  return data;
}

async function computeRiskScore(entityId: string): Promise<RiskScore> {
  const { data } = await apiClient.post('/analysis/risk', {
    entity_id: entityId,
    include_network: true,
    risk_categories: [],
  });
  return data;
}

async function runAnomalyDetection(): Promise<AnalysisResult> {
  const { data } = await apiClient.post('/analysis/anomalies', {
    entity_ids: [],
    time_window_hours: 24,
    sensitivity: 0.7,
  });
  return data;
}

async function uploadDocument(file: File): Promise<AnalysisResult> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post('/analysis/upload-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  });
  return data;
}

// Cyber Intel endpoints — aligned with actual backend routes

async function checkIOC(indicator: string): Promise<IOCResult> {
  const indicator_type = detectIOCType(indicator);
  const { data } = await apiClient.post('/cyber/ioc/check', {
    indicator,
    indicator_type,
    check_sources: [],
  });
  return data;
}

async function fetchThreats(params?: {
  severity?: string;
  threat_type?: string;
  source?: string;
  days?: number;
  page?: number;
  page_size?: number;
}): Promise<ThreatFeedResponse> {
  const { data } = await apiClient.get('/cyber/threats', { params });
  return data;
}

async function getCVEInfo(cveId: string): Promise<CVEInfo> {
  const { data } = await apiClient.get(`/cyber/vulnerabilities/${encodeURIComponent(cveId)}`);
  return data;
}

async function runScan(target: string, scanType: string = 'full'): Promise<ScanResult> {
  const { data } = await apiClient.post('/cyber/scan', {
    target,
    scan_type: scanType,
    depth: 1,
  });
  return data;
}

// ── React Query Hooks ──

export function useExtractEntities() {
  return useMutation({ mutationFn: extractEntities });
}

export function useInferRelationships() {
  return useMutation({ mutationFn: inferRelationships });
}

export function useRiskScore(entityId: string | null) {
  return useQuery({
    queryKey: ['risk-score', entityId],
    queryFn: () => computeRiskScore(entityId!),
    enabled: !!entityId,
    staleTime: 120_000,
  });
}

export function useAnomalyDetection() {
  return useMutation({ mutationFn: runAnomalyDetection });
}

export function useUploadDocument() {
  return useMutation({ mutationFn: uploadDocument });
}

export function useCheckIOC() {
  return useMutation({ mutationFn: checkIOC });
}

export function useThreats(params?: {
  severity?: string;
  days?: number;
  page?: number;
  page_size?: number;
}) {
  return useQuery({
    queryKey: ['cyber-threats', params],
    queryFn: () => fetchThreats(params),
    staleTime: 300_000,
    retry: 1,
  });
}

export function useCVEInfo(cveId: string | null) {
  return useQuery({
    queryKey: ['cve-info', cveId],
    queryFn: () => getCVEInfo(cveId!),
    enabled: !!cveId && cveId.toUpperCase().startsWith('CVE-'),
    staleTime: 600_000,
    retry: 1,
  });
}

export function useRunScan() {
  return useMutation({
    mutationFn: ({ target, scanType }: { target: string; scanType?: string }) =>
      runScan(target, scanType),
  });
}
